// Basemap tile provider for the IP location map (see Doc/NetRiskScan_MapBasemapProvider_map-v1_2026-09.md).
//
// The map is the last, optional link of the Geo chain — backend Geo result → Geo text → coordinates →
// Leaflet → basemap. Everything a provider switch needs lives here, so moving between OSM, CARTO (with
// a key), MapTiler, Stadia, a self-hosted tile server or any other Leaflet-compatible XYZ source is a
// build-variable change, never an edit to the result pages.
//
// Why not CARTO any more: since late August 2026 CARTO's basemap CDN answers keyless requests with
// tiles stamped "API KEY REQUIRED" (HTTP 200, so no load error ever fires), which is what production
// showed. A single commercial provider as the only, hard-coded basemap is the design flaw; CARTO is still
// a valid choice through the override below, with its own key and quota.

export interface MapTileProvider {
  name: string;
  // Standard Leaflet XYZ template. `{apiKey}`, when present, is filled from `apiKey` by Leaflet itself.
  urlTemplate: string;
  // HTML, rendered by Leaflet's attribution control. Required by every provider's licence.
  attribution: string;
  maxZoom: number;
  subdomains?: string;
  apiKeyRequired: boolean;
  apiKey?: string;
  // False when the provider needs a key that isn't configured: the map then shows its "unavailable"
  // state without ever creating Leaflet or sending a tile request that could only fail.
  enabled: boolean;
}

// OpenStreetMap's standard tile server: keyless, global, maintained by the OSM Foundation. It is a
// donation-funded community service, not a commercial CDN — its usage policy
// (operations.osmfoundation.org/policies/tiles) allows moderate commercial use but reserves the right to
// withdraw access, and forbids bulk download, offline use and referer-stripping. It is the default only
// because NetRiskScan's current map traffic is small; the migration path is the override below.
// Compliance points kept by LocationMap: visible attribution, no prefetch/bulk, browser caching left to
// the server's headers, and a valid Referer on every tile (see TILE_REFERRER_POLICY).
export const OSM_STANDARD_PROVIDER: MapTileProvider = {
  name: "OpenStreetMap",
  urlTemplate: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  maxZoom: 19,
  apiKeyRequired: false,
  enabled: true,
};

// The site serves `Referrer-Policy: same-origin` (Cloudflare-level, not in this repo), so without an
// override every cross-origin tile request goes out with no Referer at all — which OSM's policy treats as
// referer-stripping, and is why the site's OSM tiles were blocked in September 2026 ("App is not
// following the tile usage policy"). `strict-origin` on the tile <img>s alone sends only
// "https://www.netriskscan.com/": enough for OSM's policy and for any keyed provider's domain
// restriction, and never the page path, which on /r/… and ?ip=… pages would reveal the queried IP.
export const TILE_REFERRER_POLICY = "strict-origin";

interface MapTileEnv {
  VITE_MAP_TILE_URL?: string;
  VITE_MAP_TILE_ATTRIBUTION?: string;
  VITE_MAP_TILE_NAME?: string;
  VITE_MAP_TILE_MAX_ZOOM?: string;
  VITE_MAP_TILE_SUBDOMAINS?: string;
  VITE_MAP_TILE_API_KEY?: string;
}

// An override needs both a URL and its attribution — a provider without attribution would be a licence
// violation, so a half-configured override falls back to the default rather than shipping one.
export function resolveMapTileProvider(env: MapTileEnv): MapTileProvider {
  const urlTemplate = env.VITE_MAP_TILE_URL?.trim();
  const attribution = env.VITE_MAP_TILE_ATTRIBUTION?.trim();
  if (!urlTemplate || !attribution) return OSM_STANDARD_PROVIDER;

  const maxZoom = Number.parseInt(env.VITE_MAP_TILE_MAX_ZOOM?.trim() ?? "", 10);
  const subdomains = env.VITE_MAP_TILE_SUBDOMAINS?.trim();
  const apiKey = env.VITE_MAP_TILE_API_KEY?.trim();
  const apiKeyRequired = urlTemplate.includes("{apiKey}");
  return {
    name: env.VITE_MAP_TILE_NAME?.trim() || "Custom",
    urlTemplate,
    attribution,
    maxZoom: Number.isInteger(maxZoom) && maxZoom > 0 ? maxZoom : 18,
    ...(subdomains ? { subdomains } : {}),
    apiKeyRequired,
    ...(apiKey ? { apiKey } : {}),
    enabled: !apiKeyRequired || Boolean(apiKey),
  };
}

export const MAP_TILE_PROVIDER: MapTileProvider = resolveMapTileProvider(import.meta.env);
